import { useMemo, useState } from 'react';
import { getCoreRowModel, useReactTable, VisibilityState } from '@tanstack/react-table';
import { Building2Icon, ChevronDownIcon } from 'lucide-react';

import { CALL_COLUMNS_ADMIN } from '@/app/(app)/admin/calls/components/columns';
import { useCallFiltersCtx } from '@/app/(app)/calls/components/provider';
import { MultiSelect } from '@/components/atoms/multi-select';
import { CallDialogContent } from '@/components/call/call-dialog';
import { ColumnVisibilityPicker, DataTable } from '@/components/data-table';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet } from '@/components/ui/sheet';
import { CALL_HUMAN_REQUEST, CALL_INTENTS, CALL_OUTCOMES } from '@/consts/calls';
import { cn } from '@/lib/utils';

type CallData = ApiSchemas['CallRead'];

interface AdminCallTableProps {
  data: CallData[];
  isLoading: boolean;
}

export function AdminCallTable({ data, isLoading }: AdminCallTableProps) {
  const [openCallData, setOpenCallData] = useState<CallData | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    call_id: false,
    from_number: true,
    start_timestamp: true,
    recording_url: false,
  });

  const table = useReactTable({
    data,
    columns: CALL_COLUMNS_ADMIN,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: { columnVisibility },
  });

  return (
    <div className='space-y-3'>
      <div className='flex w-full items-center gap-2 max-lg:flex-wrap lg:justify-between'>
        <div className='flex flex-wrap items-center gap-2 max-lg:contents'>
          <AdminTableFilters />
        </div>
        <ColumnVisibilityPicker table={table} />
      </div>
      <div className={cn('overflow-hidden rounded-md border', isLoading && 'animate-pulse')}>
        <DataTable
          table={table}
          columns={CALL_COLUMNS_ADMIN}
          onRowClick={(row) => {
            if (row.original.id === openCallData?.id) {
              setOpenCallData(null);
            } else {
              setOpenCallData(row.original);
            }
          }}
          getIsRowOpen={(row) => row.original.id === openCallData?.id}
        />
      </div>
      <Sheet
        open={!!openCallData}
        onOpenChange={(v) => {
          if (!v) setOpenCallData(null);
        }}
        modal={false}
      >
        {openCallData && <CallDialogContent data={openCallData} />}
      </Sheet>
    </div>
  );
}

export function AdminTableFilters() {
  const { api } = useApiV1Ctx();
  const { data: offices = [] } = api.offices.listAllOffices.useQuery();
  const { filters, setFilters } = useCallFiltersCtx();
  const selected = useMemo(() => offices.find((b) => b.id === filters.officeId), [offices, filters.officeId]);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='outline'>
            <Building2Icon />
            <span>{selected?.name || 'Business'}</span>
            <ChevronDownIcon />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end' className='w-56'>
          {offices.map((office) => (
            <DropdownMenuCheckboxItem
              key={office.id}
              checked={filters.officeId === office.id}
              onCheckedChange={() => setFilters({ officeId: office.id })}
            >
              {office.name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <MultiSelect
        label='Category'
        selected={filters.intents}
        onChange={(values) => setFilters({ intents: values })}
        options={CALL_INTENTS}
      />
      <MultiSelect
        label='Outcome'
        selected={filters.outcomes}
        onChange={(values) => setFilters({ outcomes: values })}
        options={CALL_OUTCOMES}
      />
      <MultiSelect
        label='Human Requested'
        selected={filters.human_requested}
        onChange={(values) => setFilters({ human_requested: values })}
        options={CALL_HUMAN_REQUEST}
      />
    </>
  );
}
